// Dojo Theme Switcher — palette icon in the Navbar that opens a popover
// listing the five seasonal palettes. Each card shows its colour swatches
// plus a one-line tagline. Clicking applies the theme instantly and
// persists the choice in localStorage.

import { useEffect, useRef, useState } from "react";
import { Palette, Check, Moon, Sun } from "lucide-react";
import {
  DOJO_THEMES,
  applyDojoTheme,
  getStoredThemeId,
  setStoredThemeId,
  getStoredMode,
  setStoredMode,
} from "@/lib/dojoThemes";

export default function DojoThemeSwitcher() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(getStoredThemeId());
  const [activeMode, setActiveMode] = useState(getStoredMode());
  const ref = useRef(null);

  // Close on outside click + Esc
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (id) => {
    setActiveId(id);
    setStoredThemeId(id);
    applyDojoTheme(id, activeMode);
    setTimeout(() => setOpen(false), 350);
  };

  const pickMode = (mode) => {
    setActiveMode(mode);
    setStoredMode(mode);
    applyDojoTheme(activeId, mode);
  };

  const trigger = (
    <button
      data-testid="dojo-theme-trigger"
      onClick={() => setOpen((o) => !o)}
      title="Cambia tema del Dojo"
      className="flex items-center justify-center"
      style={{
        width: 32, height: 32,
        background: open ? "var(--bc-border, rgba(255,255,255,0.07))" : "transparent",
        border: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
        color: "var(--bc-accent)",
        transition: "all 0.18s ease",
        cursor: "pointer",
        marginLeft: 8,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bc-border, rgba(255,255,255,0.07))"; }}
      onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = "transparent"; }}
    >
      <Palette size={15} />
    </button>
  );

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {trigger}
      {open && (
        <div
          data-testid="dojo-theme-popover"
          style={{
            position: "absolute",
            top: 42,
            right: 0,
            width: 320,
            background: "rgba(8,8,10,0.96)",
            backdropFilter: "blur(22px)",
            WebkitBackdropFilter: "blur(22px)",
            border: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
            boxShadow: "0 20px 60px -15px rgba(0,0,0,0.65), 0 0 0 1px var(--bc-panel-elev, rgba(255,255,255,0.03))",
            padding: "0.85rem",
            zIndex: 80,
            animation: "dojoFadeIn 0.18s ease-out both",
          }}
        >
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.55)",
              fontWeight: 700,
              marginBottom: "0.5rem",
              paddingLeft: "0.15rem",
            }}
          >
            Dojo · Modalità
          </div>

          {/* Mode toggle Yami (Notte) ↔ Hinode (Alba) */}
          <div
            data-testid="dojo-mode-toggle"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.4rem",
              marginBottom: "0.85rem",
              padding: "0.25rem",
              background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))",
              border: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
            }}
          >
            {[
              { id: "dark", label: "Yami", sub: "Notte", Icon: Moon },
              { id: "light", label: "Hinode", sub: "Alba", Icon: Sun },
            ].map(({ id, label, sub, Icon }) => {
              const isOn = activeMode === id;
              return (
                <button
                  key={id}
                  data-testid={`dojo-mode-${id}`}
                  onClick={() => pickMode(id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.4rem",
                    padding: "0.5rem 0.4rem",
                    background: isOn
                      ? (id === "light" ? "rgba(246,198,67,0.18)" : "rgba(157,246,255,0.10)")
                      : "transparent",
                    border: `1px solid ${isOn ? (id === "light" ? "#F6C643" : "#9DF6FF") : "transparent"}`,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    color: isOn ? (id === "light" ? "#F6C643" : "#9DF6FF") : "rgba(255,255,255,0.55)",
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  <Icon size={13} />
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.05 }}>
                    <span>{label}</span>
                    <span style={{ fontSize: "0.55rem", opacity: 0.7, letterSpacing: "0.2em" }}>{sub}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.55)",
              fontWeight: 700,
              marginBottom: "0.7rem",
              paddingLeft: "0.15rem",
            }}
          >
            Dojo · Tema
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            {DOJO_THEMES.map((t) => {
              const isActive = activeId === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`dojo-theme-${t.id}`}
                  onClick={() => pick(t.id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "center",
                    gap: "0.7rem",
                    padding: "0.55rem 0.7rem",
                    background: isActive
                      ? `linear-gradient(90deg, ${t.accent}1a 0%, transparent 75%)`
                      : "rgba(255,255,255,0.02)",
                    border: `1px solid ${isActive ? t.accent : "var(--bc-border, rgba(255,255,255,0.07))"}`,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = `${t.accent}66`;
                      e.currentTarget.style.background = `${t.accent}10`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = "var(--bc-border, rgba(255,255,255,0.07))";
                      e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                    }
                  }}
                >
                  {/* Colour swatch stack */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {t.swatch.map((c, i) => (
                      <span
                        key={i}
                        style={{
                          display: "block",
                          width: 16, height: 6,
                          background: c,
                          borderRadius: 1,
                          boxShadow: i === 0 ? `0 0 6px ${t.glow}` : "none",
                        }}
                      />
                    ))}
                  </div>

                  {/* Label + tagline */}
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: "'Barlow Condensed',sans-serif",
                        fontWeight: 800,
                        fontSize: "0.92rem",
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: isActive ? t.accent : "#FFFFFF",
                        textShadow: isActive ? `0 0 10px ${t.glow}` : "none",
                      }}
                    >
                      {t.label}
                      <span
                        style={{
                          fontSize: "0.6rem",
                          letterSpacing: "0.2em",
                          opacity: 0.55,
                          marginLeft: 8,
                          fontWeight: 600,
                        }}
                      >
                        · {t.season}
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: "0.72rem",
                        color: "rgba(255,255,255,0.55)",
                        marginTop: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t.blurb}
                    </div>
                  </div>

                  {/* Active checkmark */}
                  <div style={{ width: 18, display: "flex", justifyContent: "flex-end" }}>
                    {isActive && (
                      <Check size={14} style={{ color: t.accent }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div
            style={{
              marginTop: "0.7rem",
              paddingTop: "0.55rem",
              borderTop: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.62rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.35)",
              textAlign: "center",
            }}
          >
            La scelta è salvata sul tuo dispositivo
          </div>
        </div>
      )}

      <style>{`@keyframes dojoFadeIn {
        from { opacity: 0; transform: translateY(-4px) scale(0.98); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }`}</style>
    </div>
  );
}
